/**
 * User identification utilities for consistent analytics tracking
 *
 * Centralizes user identification logic to ensure consistent
 * user properties across all analytics events
 */
import type { UserDto } from '~/api/generated';
import type posthog from 'posthog-js';
import { usePostHog } from '~/composables/usePostHog';

/**
 * Links user identity to their analytics events for accurate journey tracking
 *
 * @param posthogInstance - PostHog instance to use
 * @param user - Authenticated user data
 * @returns boolean indicating if identification was successful
 */
export const identifyUserInPostHog = (
  posthogInstance: typeof posthog | undefined | null,
  user: UserDto,
): boolean => {
  if (!posthogInstance || !user || !user.id) return false;

  try {
    // Extract email domain safely for consistent tracking
    const emailDomain =
      user.email && user.email.includes('@') ? user.email.split('@')[1] : '';

    // Create comprehensive user properties object with safe fallbacks
    const userProperties = {
      email: user.email || '',
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim(),
      first_name: user.firstName || '',
      last_name: user.lastName || '',
      user_id: user.id,
      organization_id: user.organizationId || '',
      created_at: user.createdAt || new Date().toISOString(),
      email_domain: emailDomain,
      is_admin: Boolean(user.isAdmin),
      last_login: new Date().toISOString(),
    };

    // Associate all past and future events with this user
    posthogInstance.identify(user.id, userProperties);

    // Enrich user profile for better segmentation in analytics
    posthogInstance.people.set(userProperties);

    // Set last login time for session analysis
    posthogInstance.people.set_once({
      first_login: new Date().toISOString(),
    });

    // Register super properties that will be sent with every event
    posthogInstance.register({
      user_id: user.id,
      email_domain: emailDomain,
      organization_id: user.organizationId || '',
      is_authenticated: true,
    });

    // Mark successful identification for funnel analysis
    posthogInstance.capture('user_identified', {
      user_id: user.id,
      email_domain: emailDomain,
      event_source: 'app',
      login_method: 'explicit_identification',
    });

    return true;
  } catch {
    // Silent fail in production
    return false;
  }
};

/**
 * Simplified user identification with automatic PostHog instance handling
 *
 * @param user - Authenticated user data
 * @param loginMethod - Optional method used for login (password, sso, etc.)
 * @returns boolean indicating if identification was successful
 */
export const identifyUser = (user: UserDto, loginMethod?: string): boolean => {
  if (!user) return false;

  const { posthog } = usePostHog();
  if (!posthog) return false;

  // If login method provided, capture it as a property
  if (loginMethod) {
    posthog.capture('login_success', {
      method: loginMethod,
      user_id: user.id,
      email: user.email,
    });
  }

  return identifyUserInPostHog(posthog, user);
};

/**
 * Reset user identity to anonymous state
 *
 * @returns boolean indicating if reset was successful
 */
export const resetUserIdentity = (): boolean => {
  const { posthog, reset } = usePostHog();
  if (!posthog) return false;

  try {
    // Clear super properties before resetting
    posthog.unregister('user_id');
    posthog.unregister('email_domain');
    posthog.unregister('organization_id');
    posthog.unregister('is_authenticated');

    // Reset user to anonymous state
    reset();

    // Set anonymous super properties
    posthog.register({
      is_authenticated: false,
    });

    return true;
  } catch {
    return false;
  }
};
